// ccp/src/hooks/useLiveCopilot.js
//
// Live Copilot Phase 1 (backlog register #11) — evaluates the reactive
// knowledge-answer trigger against the live contact, enforces the 30 s
// per-call floor (lib/liveCopilotTriggers.js, DEBOUNCE_MS), calls
// knktr-copilot-retrieve for the chip content, and fires shown/clicked/
// ignored instrumentation events on the same Lambda. Exposes the current
// chip (or null) and a markClicked() action for the read-only rail.
//
// It never touches the copilot Q&A panel or the coaching panel — same
// non-interference principle useCoaching.js already follows for that pair.
//
// D35 GUARD (scripts/verify-live-copilot-transcript-source.mjs, written
// before this file): the transcript this hook's triggers see is the Phase 3
// feed from TranscriptContext (useTranscript().lines), passed through
// liveCopilotView(). display.transcript is NEVER read here — it is a
// permanent [] on a live call (connectData.js).
import { useEffect, useRef, useState, useCallback } from "react";
import { useContact } from "../context/ContactContext";
import { useTranscript } from "../context/TranscriptContext";
import { apiHeaders } from "../lib/consoleApi";
import { evaluateTriggers, markFired, newLiveCopilotState, liveCopilotView, addChip, removeChip } from "../lib/liveCopilotTriggers";

const LIVE_COPILOT_URL = import.meta.env.VITE_LIVE_COPILOT_URL || "";

// Per-contact state survives a remount of the rail mid-call; dropped when
// the contact changes. Same shape as useCoaching.js's stateByContact.
const stateByContact = new Map();
function stateFor(contactId) {
  if (!stateByContact.has(contactId)) {
    stateByContact.clear(); // one live contact at a time
    stateByContact.set(contactId, newLiveCopilotState());
  }
  return stateByContact.get(contactId);
}

let chipSeq = 0;
const nextChipId = () => `chip-${Date.now()}-${++chipSeq}`;

export default function useLiveCopilot() {
  const { display } = useContact();
  const { lines } = useTranscript(); // the Phase 3 feed, the only transcript source
  const contactId = display?.meta?.contactId ?? null;
  const tenantId = display?.tenantId ?? null;
  const kbId = display?.knowledgeBaseId ?? null;
  const transcriptLen = lines.length;

  const [chips, setChips] = useState([]); // newest first, max MAX_CHIPS: { id, trigger, question, hits, tier, at }
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0); // bumped when the 30 s floor expires

  const inFlight = useRef(false);
  const timer = useRef(null);
  const displayRef = useRef(display);
  displayRef.current = display;
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const chipsRef = useRef([]);
  chipsRef.current = chips;

  useEffect(() => {
    setChips([]);
    setError(null);
    setPending(false);
  }, [contactId]);

  const logEvent = useCallback((kind, targetChip, trigger) => {
    if (!LIVE_COPILOT_URL || !tenantId || !contactId || !targetChip) return;
    apiHeaders({ "Content-Type": "application/json" })
      .then(headers => fetch(LIVE_COPILOT_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ type: "event", tenantId, contactId, chipId: targetChip.id, kind, trigger: trigger ?? targetChip.trigger }),
      }))
      .catch(() => {}); // instrumentation is best-effort — never blocks or surfaces to the agent
  }, [tenantId, contactId]);

  // Agent clicked the chip. Logs 'clicked' and clears it — a clicked chip
  // does not decay into an 'ignored' log later.
  const markClicked = useCallback(id => {
    const c = chipsRef.current.find(x => x.id === id);
    if (!c) return;
    logEvent("clicked", c);
    setChips(cs => removeChip(cs, id));
  }, [logEvent]);

  const fire = useCallback(async (trigger, key, context) => {
    if (!contactId || inFlight.current) return;
    const state = stateFor(contactId);
    markFired(state, key); // counts against the floor even if the call fails
    if (!LIVE_COPILOT_URL) { setError("Live Copilot endpoint not configured (VITE_LIVE_COPILOT_URL)."); return; }
    if (!kbId) { setError("No knowledge base is linked to this contact."); return; }
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const headers = await apiHeaders({ "Content-Type": "application/json" });
      const res = await fetch(LIVE_COPILOT_URL, {
        method: "POST",
        headers,
        // contactId: the server takes the client from the contact, not from this request (backlog 87).
        body: JSON.stringify({ type: "retrieve", knowledgeBaseId: kbId, question: context.question, tier: trigger.tier || "customer", contactId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || `Live Copilot failed (HTTP ${res.status}).`);
      if (!Array.isArray(data.hits) || !data.hits.length) return; // nothing relevant retrieved: show nothing
      // 2026-10-20 (Storm 'go labels'): which knowledge answered - the retrieve Lambda
      // reports the tier it actually used (a client with no team KB falls back to public).
      const newChip = { id: nextChipId(), trigger: trigger.id, question: context.question, hits: data.hits, tier: data.tier === "agent" ? "agent" : "customer", at: Date.now() };
      // Up to three chips show at once (Storm 23 Oct). A chip pushed off the
      // end, still unclicked, is logged 'ignored' before it goes, never
      // silently dropped.
      const { chips: next, dropped } = addChip(chipsRef.current, newChip);
      dropped.forEach(d => logEvent("ignored", d));
      setChips(next);
      logEvent("shown", newChip, trigger.id);
    } catch (err) {
      setError(err?.message || "Live Copilot unavailable.");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, [contactId, kbId, logEvent]);

  // Evaluate whenever the transcript grows and again when the floor expires.
  // `display` and the feed are read through refs so unrelated renders do
  // not re-run this — same pattern as useCoaching.js.
  useEffect(() => {
    clearTimeout(timer.current);
    const current = displayRef.current;
    if (!contactId || !current) return undefined;
    const decision = evaluateTriggers(liveCopilotView(current, linesRef.current), stateFor(contactId));
    if (!decision) return undefined;
    if (decision.waitMs != null) {
      timer.current = setTimeout(() => setTick(t => t + 1), decision.waitMs + 50);
      return () => clearTimeout(timer.current);
    }
    fire(decision.trigger, decision.key, decision.context);
    return () => clearTimeout(timer.current);
  }, [contactId, transcriptLen, tick, fire]);

  return { chips, markClicked, pending, error, enabled: Boolean(LIVE_COPILOT_URL) };
}
